import { createContext, useContext, useMemo } from 'react';

import { type ExtractedTableJson } from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import { useLiveTableResults } from 'utils/hooks/taskTracking/useLiveTableResults';

export type ExtractedTableContextType = {
  extractedTables: ExtractedTableJson[];
  isTablesDataFetching: boolean;
  isTablesDataInitialLoading: boolean;
};

export const DEFAULT_CONTEXT: ExtractedTableContextType = {
  extractedTables: [],
  isTablesDataFetching: false,
  isTablesDataInitialLoading: false,
};

export const ExtractedTableContext =
  createContext<ExtractedTableContextType>(DEFAULT_CONTEXT);

type ExtractedTableProviderProps = {
  children: React.ReactNode;
  partFileUuids: string[];
  preloadedExtractedTables?: ExtractedTableJson[];
};

/**
 * Handles fetching all extracted tables for the given part file uuids
 */
export const ExtractedTableProvider = ({
  children,
  partFileUuids,
  preloadedExtractedTables,
}: ExtractedTableProviderProps) => {
  const shouldUsePreloadedTables = preloadedExtractedTables !== undefined;
  const memoizedPartFileUuids = useMemoizedArray(partFileUuids);

  const {
    isTablesDataFetching,
    isTablesDataInitialLoading,
    extractedTablesData,
  } = useLiveTableResults({
    partFileUuids: shouldUsePreloadedTables ? [] : memoizedPartFileUuids,
  });

  const value = useMemo(
    () => ({
      extractedTables: shouldUsePreloadedTables
        ? preloadedExtractedTables ?? []
        : extractedTablesData ?? [],
      isTablesDataFetching: shouldUsePreloadedTables
        ? false
        : isTablesDataFetching,
      isTablesDataInitialLoading: shouldUsePreloadedTables
        ? false
        : isTablesDataInitialLoading,
    }),
    [
      shouldUsePreloadedTables,
      preloadedExtractedTables,
      extractedTablesData,
      isTablesDataFetching,
      isTablesDataInitialLoading,
    ],
  );

  return (
    <ExtractedTableContext.Provider value={value}>
      {children}
    </ExtractedTableContext.Provider>
  );
};

export const useExtractedTables = () => {
  return useContext<ExtractedTableContextType>(ExtractedTableContext);
};
